import React, { useContext, useEffect, useState } from 'react';
import styles from './ListBoxItem.module.css';
import {
  ListBoxItemProps as AriaListBoxItemProps,
  ListBoxItemRenderProps,
  ListStateContext,
  UNSTABLE_createLeafComponent,
} from 'react-aria-components';
import { useOption } from '@react-aria/listbox';
import { useHover } from '@react-aria/interactions';
import { filterDOMProps, mergeProps, useObjectRef } from '@react-aria/utils';
import type { Node } from 'react-stately';
import Icon from '../Icon';
import LoadingSpinner from '../LoadingSpinner';
import VisuallyHidden from '../VisuallyHidden';
import { altoConsole } from '../../utils/console/console';

export interface ListBoxItemProps extends AriaListBoxItemProps {
  children?: React.ReactNode;
  /**
   *  Enables loading state
   */
  isLoading?: boolean;
  /**
   * Visually hidden text to show when loading for screen readers
   */
  labelLoading?: string;
  /**
   *  Announces loading state to screen readers after a delay (in ms)
   *  @default 300
   */
  announceLoadingDelay?: number;
}

// TODO: Remove this RAC collection adapter when ListBox and ComboBox use the Alto collection boundary.
export const ListBoxItem = UNSTABLE_createLeafComponent<
  object,
  ListBoxItemProps,
  HTMLElement
>(
  'item',
  function ListBoxItem(
    props: ListBoxItemProps,
    forwardedRef: React.ForwardedRef<HTMLElement>,
    item: Node<object>,
  ) {
    const {
      children,
      className,
      isLoading,
      labelLoading = 'Loading',
      announceLoadingDelay = 300,
      style,
    } = props;
    const [announce, setAnnounce] = useState(false);
    const ref = useObjectRef<HTMLElement>(forwardedRef);
    const state = useContext(ListStateContext)!;
    const { optionProps, labelProps, ...optionStates } = useOption(
      { key: item.key, 'aria-label': props['aria-label'] },
      state,
      ref,
    );
    const { hoverProps, isHovered } = useHover({
      isDisabled: !optionStates.allowsSelection && !optionStates.hasAction,
      onHoverStart: props.onHoverStart,
      onHoverChange: props.onHoverChange,
      onHoverEnd: props.onHoverEnd,
    });

    const renderProps: ListBoxItemRenderProps = {
      ...optionStates,
      isHovered,
      selectionMode: state.selectionManager.selectionMode,
      selectionBehavior: state.selectionManager.selectionBehavior,
    };
    const resolvedClassName =
      typeof className === 'function'
        ? className({ ...renderProps, defaultClassName: undefined })
        : className;
    const resolvedStyle =
      typeof style === 'function'
        ? style({ ...renderProps, defaultStyle: {} })
        : style;

    // Delay announcement if loading
    useEffect(() => {
      if (isLoading) {
        const timer = setTimeout(() => setAnnounce(true), announceLoadingDelay);
        return () => clearTimeout(timer);
      }
      setAnnounce(false);
    }, [isLoading, announceLoadingDelay]);

    useEffect(() => {
      if (!item.textValue) {
        altoConsole.warn(
          'A `textValue` prop is required for <ListBoxItem> elements with non-plain text children in order to support accessibility features such as typeahead.',
          { scope: 'ListBoxItem' },
        );
      }
    }, [item.textValue]);

    const ElementType: React.ElementType = props.href ? 'a' : 'div';
    const setElementRef: React.RefCallback<
      HTMLAnchorElement | HTMLDivElement
    > = (element) => {
      ref.current = element;
    };

    return (
      <ElementType
        {...filterDOMProps(props as Parameters<typeof filterDOMProps>[0])}
        {...mergeProps(optionProps, hoverProps)}
        ref={setElementRef}
        className={[styles['list-box-item'], resolvedClassName]
          .filter(Boolean)
          .join(' ')}
        style={resolvedStyle}
        data-loading={isLoading || undefined}
        data-selected={optionStates.isSelected || undefined}
        data-disabled={optionStates.isDisabled || undefined}
        data-hovered={isHovered || undefined}
        data-focused={optionStates.isFocused || undefined}
        data-focus-visible={optionStates.isFocusVisible || undefined}
        data-pressed={optionStates.isPressed || undefined}
        data-selection-mode={
          state.selectionManager.selectionMode === 'none'
            ? undefined
            : state.selectionManager.selectionMode
        }
      >
        <span {...labelProps} className={styles['list-box-item__label']}>
          {isLoading ? labelLoading : children}
        </span>
        {/* Dedicated live region for Assistive Technologies */}
        <VisuallyHidden>
          <span
            role="status"
            aria-live="polite"
            data-testid="listboxitem-status"
          >
            {announce ? labelLoading : ''}
          </span>
        </VisuallyHidden>
        {isLoading ? (
          <LoadingSpinner
            size="1.25x"
            className={styles['list-box-item__icon']}
          />
        ) : (
          <span className={styles['list-box-item__selected']}>
            <Icon iconName="check" scale="1x" padding="square" />
          </span>
        )}
      </ElementType>
    );
  },
);

export default ListBoxItem;
